import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PDescriptionService } from '../../services/p-description.service';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgFor, CommonModule, NgIf } from '@angular/common';
import { pDescription } from '../../interfaces/pDescription';
import { DescriptionNewComponent } from '../description-new/description-new.component';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list'
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { pTicket } from '../../interfaces/pTicket';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBar } from '@angular/material/snack-bar';
import { openSnackBar } from '../../helpers/genericFunctions';
import { PTicketService } from '../../services/p-ticket.service';
import { MatInputModule } from '@angular/material/input';
import { differences } from '../../interfaces/loggingDifferences';
import { MatDialog } from '@angular/material/dialog';
import { DescriptionEditInitialDialogComponent } from '../description-edit-initial-dialog/description-edit-initial-dialog.component';
import { DescriptionEditDialogComponent } from '../description-edit-dialog/description-edit-dialog.component';
import { UploadFilesService } from '../../services/upload-files.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from '../../interfaces/api-response';
import { CustomFile } from '../../interfaces/custom-file';
import { MatTooltip } from "@angular/material/tooltip";
import { UploadFilesComponent } from '../helpers/upload-files/upload-files.component';
import { AddFileDialogComponent } from '../add-file-dialog/add-file-dialog.component';

export interface Tile {
  color: string;
  cols: number;
  rows: number;
  text: string;
}
/**
 * offers a view of the communication history with a complete history and the initial description in a seperated field 
 */
@Component({
  selector: 'app-descriptions-history-view',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    MatCardModule,
    CommonModule,
    MatListModule,
    MatGridListModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatIcon,
    MatMenuModule,
    MatButtonModule,
    MatBadgeModule,
    MatInputModule,
    MatIconModule,
    MatTooltip
  ],
  templateUrl: './descriptions-history-view.component.html',
  styleUrl: './descriptions-history-view.component.scss'
})
export class DescriptionsHistoryViewComponent implements OnChanges {


  constructor(
    private descriptionService: PDescriptionService,
    private pTicketService: PTicketService,
    private pFileService: UploadFilesService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  panelOpenState: Boolean = true;
  initialTicketDescription: string = ""

  selectedDescription!: pDescription; // Holds the selected item

  ticketDescriptionControl = new FormControl('');

  @ViewChild('descriptionNew') descriptionNewComponent!: DescriptionNewComponent;

  @Input() PTicketID!: number;
  @Input() canUpdate: boolean = false;
  @Input() pTicket!: pTicket;
  @Input() pDescriptions!: pDescription[];

  @Output() pDescriptionAddedEmitter = new EventEmitter();

  ngOnInit() {
    this.pDescriptions
    this.ticketDescriptionControl.setValue(this.pTicket.pct_initialdescription || '');
    this.initialTicketDescription = this.pTicket.pct_initialdescription;

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pDescriptions'] && this.pDescriptions?.length) {

      this.updateFileView();
    }

  }

  updateFileView(): void {
    // Initialize files array to avoid undefined
    this.pDescriptions = this.pDescriptions.map(pd => ({ ...pd, files: [] }));

    // Create a request for each description
    const requests = this.pDescriptions.map(pd =>
      this.pFileService.findByPDescriptionID(pd.id).pipe(
        map(files => ({ ...pd, files })) // files is always CustomFile[]
      )
    );

    // Execute all requests in parallel
    forkJoin(requests).subscribe(updated => {
      this.pDescriptions = updated; // replace array reference → triggers *ngFor update
      console.log(this.pDescriptions);
      this.cdr.markForCheck();      // needed if using OnPush
    });
  }

  openFile(file: any) {
    this.pFileService.downloadFile(file.id, file.pfi_filename.split('/').pop())
  }

  deleteFile(file: any, description: pDescription) {
    this.pFileService.deleteFile(file.id).subscribe({
      next: () => {
        // Replace the pDescription object in the parent array
        this.pDescriptions = this.pDescriptions.map(pd => {
          if (pd.id === description.id) {
            return {
              ...pd,
              files: pd.files?.filter(f => f.id !== file.id) || []
            };
          }
          return pd;
        });

        openSnackBar('File deleted successfully', 2000, this.snackBar);
        this.updateFileView();
      },
      error: err => {
        console.error(err);
        openSnackBar('Error deleting file', 5000, this.snackBar);
      }
    });
  }

  pTicketClosed(): boolean {
    if (this.pTicket.pct_status === 'closed') {
      return true;
    }
    else {
      return false;
    }
  }

  /**
   * Fires when a description is added. Is used to reload the ticket and it's descriptions in pTicketDetailComponent to refresh the components content
   * @param msg 
   */
  pDescriptionAddedEventFired(msg: any) {
    this.pDescriptionAddedEmitter.emit()
  }

  onUpdateDescriptionText(descripiton: pDescription) {
    const dialogRef = this.dialog.open(DescriptionEditDialogComponent, {
      data: { description: descripiton },
      width: '100%',
      maxWidth: '800px',
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe((result: pTicket | undefined) => {
      if (result) {
        this.pDescriptionAddedEmitter.emit();
      }
    });
  }

  onEditInitialDescription(event: Event): void {
    const dialogRef = this.dialog.open(DescriptionEditInitialDialogComponent, {
      data: { ticket: this.pTicket },
      width: '100%',
      maxWidth: '800px',
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe((result: pTicket | undefined) => {
      if (result) {
        this.pDescriptionAddedEmitter.emit();
      }
    });


  }

  onUpdatePTicketInitialDescription(): void {
    let diff: differences[] = [{ key: "pct_initialdescription", value1: this.initialTicketDescription, value2: this.ticketDescriptionControl.value || "Initial Description missing" }];
    let success: string = "Initial Description successfully updated.";
    let error: string = "Something went wrong while updating the iinitial description.";

    this.pTicketService.updatePTicket(this.pTicket.id, { pct_initialdescription: this.ticketDescriptionControl.value || "Initial Description missing" }, diff, success, error).subscribe(() => {

    });

  }

  onTextareaKeydown(event: KeyboardEvent): void {
    // handle linebreaks in tickets
    if (event.key === "Enter") {
      event.preventDefault(); // Prevents new line
      this.onUpdatePTicketInitialDescription(); // Trigger button click function
    }
  }
  /**
 * edit existing description
 * 
 */
  onUpdateDescription(data: any) {
    // only do sth, if the value actually changes!
    if (data.pde_type != this.selectedDescription.pde_type) {
      this.descriptionService.update(data, this.selectedDescription.id).subscribe(res => {
        // check if the update is a solution and update the ticket aswell
        if (data.pde_type === "Solution") {
          this.pTicketService.update(this.pTicket.id, { pct_has_solution: "true" }).subscribe(res => {
          });
        }
        else {
          this.updateSolutionFlag();
        }
        openSnackBar("Description updated successfully", 2000, this.snackBar);
        this.moveMessage(data)
      },
        error => {
          openSnackBar("Error while updating description" + error, 5000, this.snackBar);
        }
      )
    }
  }

  formatDateTime(timestamp: string): string {
    const date = new Date(timestamp);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  updateSolutionFlag(): void {
    let solutions = this.pDescriptions.filter(pdescription => pdescription.pde_type === "Solution")
    if (solutions.length == 1) {
      // if not, then remove the flag
      this.pTicketService.update(this.pTicket.id, { pct_has_solution: "false" }).subscribe();
    }
  }

  onUploadFile(description: pDescription): void {
    const dialogRef = this.dialog.open(AddFileDialogComponent, {
      data: { description: description, pTicketID: this.pTicket.id },
      width: '100%',
      maxWidth: '800px',
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.updateFileView();
      }
    })
  }
  onDeleteDescription(descripiton: pDescription) {

    this.descriptionService.delete(descripiton.id).subscribe(res => {
      descripiton.files.forEach(file => {
        this.pFileService.deleteFile(file.file.id).subscribe(res => { });
      })
      this.updateSolutionFlag();
      openSnackBar("Description deleted successfully", 2000, this.snackBar);
      this.pDescriptionAddedEmitter.emit();
    },
      error => {
        openSnackBar("Error while deleting description" + error, 5000, this.snackBar);
      })
  }


  setSelectedDescription(description: pDescription) {
    this.selectedDescription = description; // Store the clicked item
  }
  isMorphing = false;
  moveMessage(item: any) {
    this.isMorphing = true
    // moves message before reloading to update
    if (item.pde_type === 'User') {
      this.selectedDescription.pde_type = 'User';
      item.pde_type = 'User';
    } else if (item.pde_type === 'Support') {
      this.selectedDescription.pde_type = 'Support';
      item.pde_type = 'Support';
    } else if (item.pde_type === "ChatBot") {
      this.selectedDescription.pde_type = 'ChatBot';
      item.pde_type = 'ChatBot';
    } else if (item.pde_type === "Solution") {
      this.selectedDescription.pde_type = 'Solution';
      item.pde_type = 'Solution'
    }

    setTimeout(() => {
      this.isMorphing = false
    }, 500); // Matches transition duration
  }


}
